/**
 * Phase 14.9 W2 — BaselineAirframePicker.
 *
 * Mirror of `BaselineScenarioPicker`'s shape. Lists tenant-scoped
 * airframes filtered by role ("interceptor" | "target") so the
 * operator can pick the AE Interceptor (or any other seeded /
 * imported airframe) for a campaign.
 *
 * Backend: GET /api/sim/airframes?scope=tenant — client-side filters
 * by ``role`` (the API doesn't yet support a ?role= query param;
 * filtering here keeps the surface tight). Verified airframes float
 * to the top so the operator hits a working pick on the default
 * selection.
 *
 * Form value: a single airframe id string (or "" for unset). Parent
 * (NewCampaignWizardPage) drives both interceptor + target instances.
 */
import { useEffect, useState } from "react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { sim } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

const ROLE_LABELS = Object.freeze({
    interceptor: "Interceptor airframe",
    target: "Target airframe",
});

const ROLE_FILTERS = Object.freeze({
    interceptor: (a) => a?.role === "interceptor",
    // Threats / targets cover both 'threat' and 'target' role values
    // depending on how the airframe was seeded. The compiler reads
    // them out of the scenario's threat[*].airframe_id, so any non-
    // interceptor airframe is a candidate for "target".
    target: (a) => a?.role && a.role !== "interceptor",
});

function _verificationRank(a) {
    // Verified first, then warnings, then unverified, then failed.
    const state = a?.verification?.state;
    if (state === "verified") return 0;
    if (state === "warnings") return 1;
    if (state === "unverified") return 2;
    return 3;
}

export default function BaselineAirframePicker({
    role = "interceptor",
    value,
    onChange,
    disabled = false,
}) {
    const [airframes, setAirframes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await sim.airframes.list({ scope: "tenant" });
                if (cancelled) return;
                const rows = (Array.isArray(list) ? list : list?.items || [])
                    .filter(ROLE_FILTERS[role] || (() => true))
                    .sort((a, b) => {
                        const r = _verificationRank(a) - _verificationRank(b);
                        if (r !== 0) return r;
                        if (a.is_preset !== b.is_preset) {
                            return a.is_preset ? -1 : 1;
                        }
                        return (a.name || "").localeCompare(b.name || "");
                    });
                setAirframes(rows);
                // Auto-pick the first verified airframe of this role
                // when the form value is empty AND there's a verified
                // candidate. This satisfies the "pre-select first
                // verified airframe of each role" pre-auth from W2.1.
                if (!value && rows.length > 0 && _verificationRank(rows[0]) === 0) {
                    onChange?.(rows[0].id);
                }
            } catch (e) {
                if (!cancelled) {
                    setErr(formatApiError(e, "Failed to load airframes"));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    const label = ROLE_LABELS[role] || "Airframe";
    const triggerTestid = `airframe-picker-${role}-trigger`;
    const optionTestidPrefix = `airframe-picker-${role}-option-`;

    return (
        <div className="space-y-1" data-testid={`airframe-picker-${role}`}>
            <Label htmlFor={`airframe-picker-${role}-select`}>{label}</Label>
            <Select
                value={value || ""}
                onValueChange={onChange}
                disabled={disabled || loading}
            >
                <SelectTrigger
                    id={`airframe-picker-${role}-select`}
                    data-testid={triggerTestid}
                    className="bg-ux-bg-1"
                >
                    <SelectValue
                        placeholder={
                            loading
                                ? "Loading airframes…"
                                : `Pick ${role === "interceptor" ? "an interceptor" : "a target"} airframe`
                        }
                    />
                </SelectTrigger>
                <SelectContent className="bg-ux-bg-1 border-ux-border-1 max-h-[320px]">
                    {airframes.map((a) => {
                        const state = a?.verification?.state || "unverified";
                        return (
                            <SelectItem
                                key={a.id}
                                value={a.id}
                                data-testid={`${optionTestidPrefix}${a.id}`}
                            >
                                <span className="flex items-center gap-2">
                                    <span className="font-medium">{a.name}</span>
                                    {a.is_preset && (
                                        <span className="text-[10px] uppercase tracking-wider text-ux-info bg-ux-info-soft px-1.5 py-0.5 rounded">
                                            preset
                                        </span>
                                    )}
                                    <span className="text-[10px] text-ux-text-3 ml-auto">
                                        {state}
                                    </span>
                                </span>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
            {err && (
                <p
                    className="text-[11px] text-ux-danger"
                    data-testid={`airframe-picker-${role}-error`}
                >
                    {err}
                </p>
            )}
            <p className="text-[11px] text-ux-text-3">
                {role === "interceptor"
                    ? "Pick the interceptor airframe for this campaign. Verified airframes appear first."
                    : "Pick the target airframe (threat). Verified airframes appear first."}
            </p>
        </div>
    );
}
